import { useEffect, useState } from 'react';
import { Pencil, Eye, EyeOff } from 'lucide-react';
import {
  UniversalPage,
  Card,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  FormField,
  NotificationCard,
  StatusBadge,
  useToast,
} from '@afrilink/ui';
import {
  adminSettingsService,
  type ServicePlanRow,
} from '@/domains/settings/services/adminSettingsService';
import { formatZAR } from '@/shared/utils/format';

export default function PlansPage() {
  const { toast } = useToast();
  const [rows, setRows] = useState<ServicePlanRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ServicePlanRow | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  function reload() {
    adminSettingsService
      .listPlans()
      .then(setRows)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }

  useEffect(reload, []);

  async function quickToggle(plan: ServicePlanRow) {
    if (togglingId) return;
    setTogglingId(plan.id);
    try {
      await adminSettingsService.updatePlan(plan.id, { is_active: !plan.is_active });
      toast({ tone: 'success', title: plan.is_active ? 'Plan deactivated' : 'Plan activated', body: plan.name });
      reload();
    } catch (e) {
      toast({ tone: 'danger', title: 'Toggle failed', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setTogglingId(null);
    }
  }

  return (
    <UniversalPage
      title="Hosting plans"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Settings', href: '/admin/settings' },
        { label: 'Plans' },
      ]}
    >
      <Card interactive={false} className="border-info/30 bg-info/5 p-4 text-sm">
        <p>
          Plans appear in the order flows (sales Add Customer, Order hosting) + plan-change quote. The{' '}
          <code>whm_package_name</code> must match a package on the WHM server (verify in
          /admin/customers via cPanel SSO). Recurring amount is the total billed per cycle —
          e.g. R129 monthly, R1290 annual. Setup fee is one-off, charged on first invoice.
        </p>
      </Card>

      {error && <NotificationCard tone="danger" title="Could not load plans" body={error} />}

      <Card interactive={false}>
        <div className="divide-y divide-border">
          {rows.map((p) => (
            <div key={p.id} className="grid grid-cols-1 items-center gap-3 p-4 sm:grid-cols-12">
              <div className="sm:col-span-4">
                <p className="text-sm font-semibold text-foreground">{p.name}</p>
                <p className="text-xs text-muted-foreground">
                  <code className="font-sans">{p.slug}</code> · WHM:{' '}
                  <code className="font-sans">{p.whm_package_name}</code>
                </p>
              </div>
              <div className="sm:col-span-3">
                <p className="text-sm font-semibold text-foreground">{formatZAR(p.recurring_amount)}</p>
                <p className="text-xs text-muted-foreground">/ {p.billing_cycle}</p>
              </div>
              <div className="sm:col-span-2">
                <StatusBadge tone={p.is_active ? 'active' : 'draft'}>
                  {p.is_active ? 'Active' : 'Inactive'}
                </StatusBadge>
              </div>
              <div className="flex justify-end gap-2 sm:col-span-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => quickToggle(p)}
                  loading={togglingId === p.id}
                  disabled={togglingId !== null}
                >
                  {p.is_active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  {p.is_active ? 'Deactivate' : 'Activate'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditing(p)}>
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
              </div>
            </div>
          ))}
          {rows.length === 0 && !error && (
            <p className="p-8 text-center text-sm text-muted-foreground">Loading…</p>
          )}
        </div>
      </Card>

      {editing && (
        <PlanEditModal
          plan={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </UniversalPage>
  );
}

function PlanEditModal({
  plan,
  onClose,
  onSaved,
}: {
  plan: ServicePlanRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [name, setName] = useState(plan.name);
  const [description, setDescription] = useState(plan.description ?? '');
  const [recurringAmount, setRecurringAmount] = useState(String(plan.recurring_amount));
  const [setupFee, setSetupFee] = useState(String(plan.setup_fee));
  const [whmPackageName, setWhmPackageName] = useState(plan.whm_package_name);
  const [displayOrder, setDisplayOrder] = useState(String(plan.display_order));
  const [saving, setSaving] = useState(false);

  async function save() {
    const recurring = Number(recurringAmount);
    const setup = Number(setupFee);
    const order = Number(displayOrder);
    if (!Number.isFinite(recurring) || recurring < 0) {
      toast({ tone: 'danger', title: 'Invalid recurring_amount' });
      return;
    }
    if (!Number.isFinite(setup) || setup < 0) {
      toast({ tone: 'danger', title: 'Invalid setup_fee' });
      return;
    }
    if (!Number.isInteger(order)) {
      toast({ tone: 'danger', title: 'display_order must be integer' });
      return;
    }
    setSaving(true);
    try {
      await adminSettingsService.updatePlan(plan.id, {
        name: name.trim(),
        description: description.trim() || null,
        recurring_amount: recurring,
        setup_fee: setup,
        whm_package_name: whmPackageName.trim(),
        display_order: order,
      });
      toast({ tone: 'success', title: 'Plan saved', body: name });
      onSaved();
    } catch (e) {
      toast({ tone: 'danger', title: 'Save failed', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={true}
      onOpenChange={(v) => !v && onClose()}
      title={`Edit plan · ${plan.name}`}
      description={`slug: ${plan.slug} · billing_cycle: ${plan.billing_cycle} (immutable)`}
      size="wide"
    >
      <ModalBody>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Display name" htmlFor="p-name">
            <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} disabled={saving} />
          </FormField>
          <FormField label="WHM package name" htmlFor="p-whm" helper="Must match a package on the WHM server.">
            <Input id="p-whm" value={whmPackageName} onChange={(e) => setWhmPackageName(e.target.value)} disabled={saving} className="text-sm" />
          </FormField>
          <FormField label="Recurring amount (ZAR)" htmlFor="p-rec">
            <Input id="p-rec" type="number" step="0.01" value={recurringAmount} onChange={(e) => setRecurringAmount(e.target.value)} disabled={saving} />
          </FormField>
          <FormField label="Setup fee (ZAR)" htmlFor="p-setup">
            <Input id="p-setup" type="number" step="0.01" value={setupFee} onChange={(e) => setSetupFee(e.target.value)} disabled={saving} />
          </FormField>
          <FormField label="Display order" htmlFor="p-order" helper="Lower = appears first in customer pickers.">
            <Input id="p-order" type="number" step="1" value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)} disabled={saving} />
          </FormField>
          <FormField label="Description" htmlFor="p-desc">
            <Input id="p-desc" value={description} onChange={(e) => setDescription(e.target.value)} disabled={saving} />
          </FormField>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={save} loading={saving} disabled={saving}>Save plan</Button>
      </ModalFooter>
    </Modal>
  );
}
